/**
 * Settings Page
 *
 * Allows users to manage their custom mood configurations and recommendation settings.
 */
import React, { useState, useEffect } from 'react';
import CustomMoodBuilder from '@/components/CustomMoodBuilder';
import RecommendationSettings from '@/components/RecommendationSettings';
import { authenticatedFetch } from '@/lib/auth';
import { useToast } from '@/contexts/ToastContext';

interface CustomMood {
  id: number;
  name: string;
  emoji?: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  genre_tags?: string[];
}

type ViewMode = 'list' | 'create' | 'edit';
type SettingsTab = 'recommendations' | 'custom-moods';

const Settings: React.FC = () => {
  const [moods, setMoods] = useState<CustomMood[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedMoodId, setSelectedMoodId] = useState<number | undefined>();
  const [activeTab, setActiveTab] = useState<SettingsTab>('recommendations');
  const { success, error: showError } = useToast();

  useEffect(() => {
    if (viewMode === 'list') {
      loadMoods();
    }
  }, [viewMode]);

  const loadMoods = async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch('/api/moods');
      if (!response.ok) throw new Error('Failed to load moods');
      const data = await response.json();
      setMoods(data.moods || []);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to load moods');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this custom mood?')) return;

    try {
      const response = await authenticatedFetch(`/api/moods/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete mood');

      setMoods(moods.filter(m => m.id !== id));
      success('Mood deleted');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete mood');
    }
  };

  const handleToggleActive = async (mood: CustomMood) => {
    try {
      const response = await authenticatedFetch(`/api/moods/${mood.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !mood.is_active }),
      });

      if (!response.ok) throw new Error('Failed to update mood');

      setMoods(moods.map(m => m.id === mood.id ? { ...m, is_active: !m.is_active } : m));
      success(mood.is_active ? 'Mood deactivated' : 'Mood activated');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update mood');
    }
  };

  const handleEdit = (id: number) => {
    setSelectedMoodId(id);
    setViewMode('edit');
  };

  const handleCreate = () => {
    setSelectedMoodId(undefined);
    setViewMode('create');
  };

  const handleSaveComplete = () => {
    setViewMode('list');
    setSelectedMoodId(undefined);
  };

  const handleCancel = () => {
    setViewMode('list');
    setSelectedMoodId(undefined);
  };

  if (viewMode === 'create' || viewMode === 'edit') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <CustomMoodBuilder
            moodId={selectedMoodId}
            onSave={handleSaveComplete}
            onCancel={handleCancel}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Settings</h1>
        <p className="text-gray-400">Customize your music recommendations and preferences</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 border-b border-gray-700 pb-4">
        <button
          onClick={() => setActiveTab('recommendations')}
          className={`px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium transition-colors text-sm md:text-base ${
            activeTab === 'recommendations'
              ? 'bg-spotify-green text-black'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
        >
          🎯 Recommendations
        </button>
        <button
          onClick={() => setActiveTab('custom-moods')}
          className={`px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium transition-colors text-sm md:text-base ${
            activeTab === 'custom-moods'
              ? 'bg-spotify-green text-black'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
        >
          🎨 Custom Moods
        </button>
      </div>

        {/* Recommendation Settings Tab */}
        {activeTab === 'recommendations' && (
          <RecommendationSettings />
        )}

        {/* Custom Moods Tab */}
        {activeTab === 'custom-moods' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">Custom Moods</h2>
                <p className="text-gray-400 text-sm">Create and manage your own mood profiles</p>
              </div>
              <button
                onClick={handleCreate}
                className="bg-spotify-green hover:bg-spotify-green-dark text-black font-semibold px-6 py-3 rounded-lg transition-colors flex items-center gap-2"
              >
                <span className="text-xl">+</span>
                Create Custom Mood
              </button>
            </div>

          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="text-gray-400 text-lg">Loading moods...</div>
            </div>
          ) : moods.length === 0 ? (
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-12 text-center">
              <div className="text-6xl mb-4">🎵</div>
              <h3 className="text-xl font-semibold text-white mb-2">No Custom Moods Yet</h3>
              <p className="text-gray-400 mb-6">
                Create your first custom mood to personalize your music experience
              </p>
              <button
                onClick={handleCreate}
                className="bg-spotify-green hover:bg-spotify-green-dark text-black font-semibold px-6 py-3 rounded-lg transition-colors"
              >
                Create Your First Mood
              </button>
            </div>
          ) : (
            <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-800 border-b border-gray-700">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-300">Mood</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-300">Description</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-300">Genres</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-gray-300">Status</th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-gray-300">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {moods.map((mood) => (
                    <tr key={mood.id} className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {mood.emoji && <span className="text-2xl">{mood.emoji}</span>}
                          <span className="text-white font-medium">{mood.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-400 text-sm line-clamp-2">
                          {mood.description || '-'}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        {mood.genre_tags && mood.genre_tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {mood.genre_tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="bg-gray-800 border border-gray-700 rounded-full px-2 py-0.5 text-xs text-gray-300"
                              >
                                {tag}
                              </span>
                            ))}
                            {mood.genre_tags.length > 3 && (
                              <span className="text-xs text-gray-500">
                                +{mood.genre_tags.length - 3} more
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-600 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleActive(mood)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                            mood.is_active
                              ? 'bg-green-900/50 text-green-400 border border-green-700 hover:bg-green-900/70'
                              : 'bg-gray-800 text-gray-500 border border-gray-700 hover:bg-gray-700'
                          }`}
                        >
                          {mood.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEdit(mood.id)}
                            className="bg-gray-800 hover:bg-gray-700 text-white px-3 py-1.5 rounded transition-colors text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(mood.id)}
                            className="bg-red-900/50 hover:bg-red-900/70 text-red-400 px-3 py-1.5 rounded transition-colors text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

            <div className="mt-6 text-center text-gray-500 text-sm">
              {moods.length} / 50 custom moods
            </div>
          </div>
        )}
    </div>
  );
};

export default Settings;
